import { useEffect, useState } from 'react';
import { getExperienceNames, subscribeExperience, subscribeExperiences } from '@/data/experiencesRepo';
import type { ExperienceType } from '@/types/types_experience';

/**
 * Subscribes to all experiences in real time via a Firestore snapshot listener,
 * so the UI reflects data changes without a manual reload.
 */
export function useExperiences() {
  const [experiences, setExperiences] = useState<ExperienceType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeExperiences((list) => {
      setExperiences(list);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  return { experiences, loading };
}

/**
 * The names of the experiences named, keyed by id — an empty map until they
 * arrive, so a caller renders the id and then the title.
 *
 * A one-shot read, not a subscription: a title changing while somebody reads a
 * chart is not worth a listener, let alone one on the whole catalogue.
 *
 * Keyed on the joined ids rather than the array, since callers derive that array
 * inside a render and a new array of the same ids must not re-fetch.
 */
export function useExperienceNames(ids: string[]) {
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const key = ids.join(',');

  useEffect(() => {
    let cancelled = false;
    getExperienceNames(key ? key.split(',') : [])
      .then((found) => {
        if (!cancelled) setNames(found);
      })
      .catch((e: unknown) => {
        console.error('Experience name lookup failed', e);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return names;
}

/** Subscribes to a single experience by id in real time. */
export function useExperience(id: string | undefined) {
  const [experience, setExperience] = useState<ExperienceType>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      // Defer to a microtask so we don't setState synchronously in the effect.
      void Promise.resolve().then(() => {
        if (cancelled) return;
        setExperience(undefined);
        setLoading(false);
      });
      return () => {
        cancelled = true;
      };
    }
    const unsubscribe = subscribeExperience(id, (exp) => {
      setExperience(exp);
      setLoading(false);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [id]);

  return { experience, loading };
}
